import React, { useEffect, useMemo, useRef } from 'react';
import {
  HStack, VStack, Text, Box, Pressable, Icon,
} from '@gluestack-ui/themed';
import { AlertCircle } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { AIPresenceOrb } from '@/common/components/presence/AIPresenceOrb';
import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';
import type { EvaluationStreamProgress } from '@/student/task/stores/TaskSubmissionStore';
import type { StudentEvaluationFeedback } from '@/common/api/models';

import {
  deriveActivePresencePhraseKey,
  deriveAiTutorPresence,
  deriveStageAccessibilityLabelKey,
} from './aiTutorPresenceState';
import AiTutorPresenceShimmerText, {
  type AiTutorPresenceShimmerVariant,
} from './AiTutorPresenceShimmerText';
import AiTutorFormativeResponse from './AiTutorFormativeResponse';

type FormativeFeedbackData = {
  observation: string;
  nextStep: string;
  isReadyToProgress?: boolean;
};

type Props = {
  progress: EvaluationStreamProgress | null;
  errorMessage?: string | null;
  formativeFeedback?: FormativeFeedbackData | null;
  formativeFeedbackVersion?: number;
  formativeFeedbackRunId?: string | null;
  initialStudentFeedback?: StudentEvaluationFeedback;
  courseId?: string;
  moduleId?: string;
  taskId?: string;
  readonly?: boolean;
  hideStudentFeedbackControls?: boolean;
  activityPulse?: number;
  onCancel?: () => void;
  onRetry?: () => void;
};

const ACTION_MIN_HEIGHT = 36;

const SHIMMER_MODES = new Set(['evaluating', 'writingFeedback', 'extendedWait']);

const AiTutorPresenceBand: React.FC<Props> = ({
  progress,
  errorMessage,
  formativeFeedback,
  formativeFeedbackVersion = 0,
  formativeFeedbackRunId = null,
  initialStudentFeedback,
  courseId = '',
  moduleId = '',
  taskId = '',
  readonly = false,
  hideStudentFeedbackControls = false,
  activityPulse = 0,
  onCancel,
  onRetry,
}) => {
  const { t } = useTranslation();
  const reducedMotion = useOrbReducedMotion();
  const announcedVersionRef = useRef<number | null>(null);
  const announcedStageRef = useRef<string | null>(null);
  const hasCompleteFeedback = Boolean(formativeFeedback);
  const viewModel = useMemo(
    () => deriveAiTutorPresence(progress, { errorMessage, hasCompleteFeedback }),
    [progress, errorMessage, hasCompleteFeedback],
  );

  const elapsedMs = progress?.elapsedMs ?? 0;
  const phraseKey = deriveActivePresencePhraseKey(
    viewModel.statusMode,
    elapsedMs,
    reducedMotion,
  );
  const stageAnnouncementKey = deriveStageAccessibilityLabelKey(viewModel.statusMode);

  const primaryLabel = (() => {
    if (viewModel.statusMode === 'error') {
      return t('student.task.aiTutor.presenceBand.evaluationFailed');
    }
    if (phraseKey) return t(phraseKey);
    return '';
  })();

  const errorDetail = viewModel.statusMode === 'error' ? errorMessage : null;

  const showShimmer = !reducedMotion && SHIMMER_MODES.has(viewModel.statusMode);
  const shimmerVariant: AiTutorPresenceShimmerVariant = viewModel.statusMode === 'extendedWait'
    ? 'extendedWait'
    : 'active';

  const liveRegionLabel = (() => {
    if (viewModel.statusMode === 'response' && formativeFeedback) {
      return `${t('student.task.aiTutor.presenceBand.whatISee')}, ${formativeFeedback.observation}. ${t('student.task.aiTutor.presenceBand.suggestedNextStep')}, ${formativeFeedback.nextStep}.`;
    }
    if (stageAnnouncementKey) return t(stageAnnouncementKey);
    if (errorDetail) return `${primaryLabel}, ${errorDetail}`;
    return primaryLabel;
  })();

  const shouldAnnounceResponse = viewModel.statusMode === 'response'
    && formativeFeedbackVersion > 0
    && announcedVersionRef.current !== formativeFeedbackVersion;

  const shouldAnnounceStage = viewModel.statusMode !== 'response'
    && stageAnnouncementKey
    && announcedStageRef.current !== viewModel.statusMode;

  useEffect(() => {
    if (shouldAnnounceResponse) {
      announcedVersionRef.current = formativeFeedbackVersion;
    }
  }, [shouldAnnounceResponse, formativeFeedbackVersion]);

  useEffect(() => {
    if (shouldAnnounceStage) {
      announcedStageRef.current = viewModel.statusMode;
    }
  }, [shouldAnnounceStage, viewModel.statusMode]);

  const bandBg = viewModel.statusMode === 'error'
    ? '$bgSubtle'
    : '$bgBrandSubtle';

  return (
    <Box
      bg={bandBg}
      borderBottomWidth={1}
      borderColor="$borderLight"
      testID="ai-tutor-presence-band"
    >
      <HStack
        px="$6"
        py="$4"
        alignItems="center"
        space="md"
        testID="ai-tutor-presence-anchor-row"
      >
        <AIPresenceOrb
          state={viewModel.orbState}
          size={56}
          activityPulse={activityPulse}
          testID="ai-tutor-presence-orb"
        />
        {viewModel.statusMode !== 'response' ? (
          <VStack
            flex={1}
            space="xs"
            accessibilityLiveRegion={shouldAnnounceStage ? 'polite' : 'none'}
            accessible={shouldAnnounceStage || viewModel.statusMode === 'error'}
            accessibilityRole="text"
            accessibilityLabel={shouldAnnounceStage || viewModel.statusMode === 'error'
              ? liveRegionLabel
              : undefined}
          >
            <Text
              testID="ai-tutor-presence-stage-announcement"
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              aria-hidden
              style={{
                position: 'absolute', width: 1, height: 1, opacity: 0,
              }}
            >
              {stageAnnouncementKey ? t(stageAnnouncementKey) : ''}
            </Text>
            <HStack space="xs" alignItems="center">
              {viewModel.statusMode === 'error' && (
                <Icon as={AlertCircle} size="sm" color="$statusError" testID="ai-tutor-presence-error-icon" />
              )}
              <AiTutorPresenceShimmerText
                testID="ai-tutor-presence-status-primary"
                showShimmer={showShimmer}
                shimmerVariant={shimmerVariant}
                phraseKey={phraseKey ?? primaryLabel}
              >
                {primaryLabel}
              </AiTutorPresenceShimmerText>
            </HStack>
            {errorDetail && (
              <Text
                size="xs"
                color="$textSubtle"
                testID="ai-tutor-presence-error-detail"
              >
                {errorDetail}
              </Text>
            )}
          </VStack>
        ) : (
          <Box
            flex={1}
            accessibilityLiveRegion={shouldAnnounceResponse ? 'polite' : 'none'}
            accessible={shouldAnnounceResponse}
            accessibilityRole="text"
            accessibilityLabel={shouldAnnounceResponse ? liveRegionLabel : undefined}
          />
        )}
        {viewModel.statusMode === 'extendedWait' && onCancel && (
          <Pressable
            onPress={onCancel}
            minHeight={ACTION_MIN_HEIGHT}
            px="$3"
            justifyContent="center"
            borderRadius="$md"
            sx={{ ':hover': { bg: '$bgHover' }, ':focusVisible': { outlineColor: '$borderFocus' } }}
            testID="ai-tutor-presence-cancel"
            accessibilityRole="button"
            accessibilityLabel={t('common.buttons.cancel')}
          >
            <Text size="sm" color="$textBody" fontWeight="medium">
              {t('common.buttons.cancel')}
            </Text>
          </Pressable>
        )}
        {viewModel.statusMode === 'error' && onRetry && (
          <Pressable
            onPress={onRetry}
            minHeight={ACTION_MIN_HEIGHT}
            px="$3"
            justifyContent="center"
            borderRadius="$md"
            sx={{ ':hover': { bg: '$bgHover' }, ':focusVisible': { outlineColor: '$borderFocus' } }}
            testID="ai-tutor-presence-retry"
            accessibilityRole="button"
            accessibilityLabel={t('common.buttons.retry')}
          >
            <Text size="sm" color="$textBody" fontWeight="medium">
              {t('common.buttons.retry')}
            </Text>
          </Pressable>
        )}
      </HStack>
      {viewModel.statusMode === 'response' && formativeFeedback ? (
        <AiTutorFormativeResponse
          variant="presence"
          animateEntry
          courseId={courseId}
          moduleId={moduleId}
          taskId={taskId}
          observation={formativeFeedback.observation}
          nextStep={formativeFeedback.nextStep}
          isReadyToProgress={formativeFeedback.isReadyToProgress}
          formativeFeedbackVersion={formativeFeedbackVersion}
          runId={formativeFeedbackRunId}
          initialFeedback={initialStudentFeedback}
          readonly={readonly}
          hideStudentFeedbackControls={hideStudentFeedbackControls}
        />
      ) : null}
    </Box>
  );
};

export default AiTutorPresenceBand;
